import { AddressInputAddress } from 'lib/components/Form/AddressInput/AddressInput'

import {
  BookingFlowProduct,
  PackageAndProtectionCoverageOption,
  TravelerDetailsItemData,
} from '@/app/book/stores/useCartStore'
import { TripType } from '@/app/book/stores/useShipmentStore'
import {
  OrderSummaryAddress,
  OrderSummaryOrder,
  OrderSummaryProductFee,
  OrderSummaryProductInsurance,
  OrderSummaryProductLabel,
  OrderSummaryShipment,
} from '@/types/order_summary'
import { Payment } from '@/types/payment'
import type { ShipmentDatesSegment } from '@/types/shipment'

export interface Shipment {
  type?: TripType
  cruise?: boolean
  segments: AddressInputAddress[]
  shipment_dates?: ShipmentDatesSegment[]
}

/**
 * This is a helper function to build an order object used for the order summary
 * in the sidebar of the booking flow and the booking flow /review page.
 *
 * This produces a deeply nested object given the following relationships:
 * - Order has many Shipments
 * - Order has many Travelers
 * - Traveler has many Shipments
 * - Traveler belongs to a Shipment
 */
export const generateOrderData = (
  { shipment_dates = [], segments }: Shipment,
  selectedProducts: BookingFlowProduct[],
  travelers: TravelerDetailsItemData[],
  payment?: Payment,
): Partial<Order> => {
  const legs = shipment_dates.map((date, index) =>
    buildOrderLeg(selectedProducts, segments, travelers, date, index),
  )

  return {
    id: '',
    created_at: new Date().toLocaleDateString('en-US'), // TODO: remove hardcoded date
    legs: legs,
    // TODO: remove hardcoded data
    user: {
      id: '',
      first_name: payment?.firstName,
      last_name: payment?.lastName,
      country_code: payment?.billingCountry,
    },
    transactions: [
      {
        payment_method: {
          card_brand: '',
          last4: '',
          created_at: new Date().toLocaleDateString('en-US'),
          display_name: payment ? [payment.cardType, `XXXX${payment.last4}`].join(' - ') : '',
        } as CreditCardPaymentMethod,
        type: '',
        transaction_id: '',
        value: 0,
        state: 'successful',
      },
    ],
  }
}

/**
 * Helper function to build a segment(Leg) for an order:
 * 1. It builds the route for the leg.
 * 2. Per traveler, it maps the selected products to shipments
 * @returns {OrderLeg} - An order leg object
 */
const buildOrderLeg = (
  products: BookingFlowProduct[],
  addresses: AddressInputAddress[],
  travelers: TravelerDetailsItemData[],
  shipmentDate: ShipmentDatesSegment,
  index: number,
): OrderLeg => {
  // TODO: only works for one way or round trip
  const legName = index === 0 ? 'Outbound' : 'Return'

  return {
    leg_id: `leg_${index + 1}`,
    leg_index: index + 1,
    leg_name: legName,
    origin: buildOrderLegAddress(addresses, `address_${index + 1}`, index === 0 ? 0 : 1),
    destination: buildOrderLegAddress(addresses, `address_${index + 1}`, index === 0 ? 1 : 0),
    pickup_fee_cents: 0, // TODO: handling options
    travelers: travelers.map(
      (traveler: TravelerDetailsItemData): OrderLegTraveler => ({
        id: traveler.email ? `traveler-${traveler.email}` : `traveler-${index}`,
        name: traveler.name || '',
        email: traveler.email || '',
        phone: traveler.phone || '',
        sms: traveler.sms || false,
        shipments: products.map((product) =>
          buildShipmentsForTraveler(shipmentDate, legName, product),
        ),
      }),
    ),
  }
}

const buildOrderLegAddress = (
  addresses: AddressInputAddress[],
  key: string,
  index: number,
): OrderAddress => {
  const address = addresses[index]

  return {
    ...address,
    country: address?.country_code?.toUpperCase(),
    id: key,
  } as OrderAddress // TODO: don't typecast
}

/**
 * This maps shipments to a leg per traveler
 * @returns {OrderShipment[]} - A list of shipments for a leg of an order
 */
const buildShipmentsForTraveler = (
  shipmentDate: ShipmentDatesSegment,
  legName: OrderShipment['shipment_leg_name'],
  product: BookingFlowProduct,
): OrderShipment => {
  const isCoverageOption = ({ value }: PackageAndProtectionCoverageOption): boolean =>
    value === product.coverageAmountCents

  const paidInsuranceCents = product.coverageAmountCents
    ? product.coverageOptions?.find(isCoverageOption)?.price || 0
    : 0

  const insuranceId = product.coverageOptions?.find(isCoverageOption)?.id

  const transitRateItem =
    shipmentDate?.items?.find(({ productId }) => productId === product.id) || null

  // TODO: map the properties of the selected service level option to the order shipment itself
  return {
    id: Math.random().toString(36).substring(2),
    carrier_tracking_id: '',
    tracking_id: '',
    ship_date: shipmentDate.shipDate || '',
    arrival_date: shipmentDate.arrivalDate || '',
    estimated_carrier_delivery_date: shipmentDate.arrivalDate || '',
    humanized_carrier_service_type: shipmentDate.serviceName || '',
    products: [
      product,
      {
        id: product.insuranceId || insuranceId || '',
        type: 'insurance',
        coverage_amount_cents: product.coverageAmountCents ?? 100000,
        product_line: product.product_line,
      },
    ],
    carrier_service_level: {
      id: '',
      display_name: '',
      carrier: {
        code: 'FDXE',
        system_name: 'FEDEX',
        display_name: 'FedEx',
      },
    },
    discount_cents: 0,
    wholesale_discount_cents: 0,
    upgrade_discount: 0,
    retail_ship_rate:
      transitRateItem?.adjustedPriceCents ||
      transitRateItem?.priceCents ||
      product.price_cents ||
      0,
    paid_insurance_cents: paidInsuranceCents,
    handling_fee_cents: 0,
    fuel_surcharge_fee_cents: 0,
    clearance_fee_cents: 0,
    total_price_cents: 0,
    shipment_leg_name: legName,
    is_dropoff: shipmentDate?.handlingOptionId === 'dropoff',
    updated_at: new Date().toLocaleString('en-US'),
  }
}

const reformatAddress = (address: OrderSummaryAddress): OrderAddress => {
  return {
    id: address.id,
    address_1: address.address1,
    address_2: address.address2,
    city: address.city,
    state: address.state,
    zip: address.zip5,
    country: address.countryCode,
    phone_number: '',
  }
}

const reformatProduct = (
  product: OrderSummaryProductFee | OrderSummaryProductInsurance | OrderSummaryProductLabel,
): OrderProductFee | OrderProductInsurance | OrderProductLabel => {
  if (product.type === 'fee') {
    return {
      type: 'fee',
    }
  } else if (product.type === 'insurance') {
    return {
      type: 'insurance',
      id: product.id,
      coverage_amount_cents: product.coverageAmountCents,
      product_line: {
        id: product.productLine?.id,
        display_name: product.productLine?.displayName,
      },
    }
  }

  return {
    type: product.type,
    id: product.id,
    display_name: product.displayName,
    full_name: product.fullName,
    name: product.name,
    weight: product.weight,
    product_line: {
      id: product.productLine?.id,
      display_name: product.productLine?.displayName,
    },
    price_cents: product.priceCents,
    packagingType: product.packagingType,
  }
}

// TODO: refactor backend to always return a default insurance product
// like the original orderService mapping
const reformatProducts = (
  products: Array<OrderSummaryProductFee | OrderSummaryProductInsurance | OrderSummaryProductLabel>,
): Array<OrderProductFee | OrderProductInsurance | OrderProductLabel> => {
  const updated = products.map(reformatProduct)
  const insurance = updated.find((product) => product.type === 'insurance')

  if (insurance) return updated

  const label = updated.find((product) => product.type === 'label')

  const defaultInsurance: OrderProductInsurance = {
    coverage_amount_cents: 0,
    id: '',
    product_line: label?.product_line || { id: '', display_name: '' },
    type: 'insurance',
  }

  return [...updated, defaultInsurance]
}

const reformatShipment = (shipment: OrderSummaryShipment): OrderShipment => {
  return {
    id: shipment.id,
    carrier_tracking_id: shipment.carrierTrackingId,
    tracking_id: shipment.trackingId,
    ship_date: shipment.shipDate,
    arrival_date: shipment.arrivalDate,
    estimated_carrier_delivery_date: shipment.estimatedCarrierDeliveryDate,
    updated_at: shipment.updatedAt,
    humanized_carrier_service_type: shipment.humanizedCarrierServiceType,
    products: reformatProducts(shipment.products),
    carrier_service_level: {
      id: shipment.carrierServiceLevel?.id,
      display_name: shipment.carrierServiceLevel?.displayName,
      carrier: {
        code: shipment.carrierServiceLevel?.carrier?.code,
        system_name: shipment.carrierServiceLevel?.carrier?.systemName,
        display_name: shipment.carrierServiceLevel?.carrier?.displayName,
      },
    },
    discount_cents: shipment.discountCents,
    wholesale_discount_cents: shipment.wholesaleDiscountCents,
    upgrade_discount: shipment.upgradeDiscount,
    retail_ship_rate: shipment.retailShipRate,
    paid_insurance_cents: shipment.paidInsuranceCents,
    handling_fee_cents: shipment.handlingFeeCents,
    fuel_surcharge_fee_cents: shipment.fuelSurchargeCents,
    clearance_fee_cents: shipment.clearanceFeeCents,
    total_price_cents: shipment.totalPriceCents,
    shipment_leg_name: shipment.shipmentLegName,
  }
}

// reformats the order summary from graphql (camelized) into the format used in the app (snake_case)
export const reformatOrderSummary = (order_summary?: OrderSummaryOrder): Partial<Order> => {
  if (!order_summary) return {}

  return {
    id: order_summary.id,
    created_at: order_summary.createdAt,
    legs: order_summary.legs?.map((leg) => {
      return {
        leg_id: leg.id,
        leg_index: leg.legIndex,
        leg_name: leg.legName,
        origin: reformatAddress(leg.origin),
        destination: reformatAddress(leg.destination),
        pickup_fee_cents: leg.pickupFeeCents,
        travelers: leg.travelers?.map((traveler) => {
          return {
            id: traveler.id,
            name: traveler.name,
            email: traveler.email,
            phone: traveler.phone,
            shipments: traveler.shipments?.map((shipment) => {
              return reformatShipment(shipment)
            }),
          }
        }),
      }
    }),
    user: {
      id: order_summary.user?.id,
      first_name: order_summary.user?.firstName,
      last_name: order_summary.user?.lastName,
      country_code: order_summary.user?.countryCode,
    },
    transactions: order_summary.transactions?.map((transaction) => {
      return {
        payment_method: {
          card_brand: transaction.paymentMethod?.cardBrand,
          last4: transaction.paymentMethod?.last4,
          created_at: transaction.paymentMethod?.createdAt,
          display_name: transaction.paymentMethod?.displayName,
        },
        type: '',
        transaction_id: '',
        value: 0,
        state: '',
      }
    }),
  }
}
